/**
 * What is actually on the screen, read off the rendered page.
 *
 * The route says which screen and which subject; this says what it is showing.
 * Read from the DOM rather than collected from each page's hooks, because a
 * page renders what it decided the user should see — already filtered, already
 * sorted, already formatted in the units on the tile. Reaching into twenty
 * screens' state to rebuild that would produce a different answer from the one
 * being looked at, which is the one the question is about.
 *
 * SHAPE IS NOT COSMETIC. The worker emits every context key into the prompt as
 * `key=<json>`, but it first runs the value through `scrub_blob_fields`, which
 * replaces any single string over 500 characters with `<blob:Nchars>`. So the
 * digest is a tree of short strings — labels, cells, headings — and never one
 * long transcript of the page. A blob would be dropped in full.
 *
 * Everything here is bounded. A screen can hold a virtualised table of a
 * thousand rows, and the question is never about row 400.
 */

/** Caps. Generous enough to describe a screen, small enough to send every turn. */
const LIMITS = {
  cell: 90,
  text: 280,
  stats: 14,
  sections: 10,
  columns: 12,
  rows: 12,
  points: 8,
  total: 14000,
};

const cut = (text, max) => (text.length > max ? `${text.slice(0, max - 1)}…` : text);

const clean = (node, max = LIMITS.cell) =>
  cut((node?.textContent ?? '').replace(/\s+/g, ' ').trim(), max);

/**
 * A composite node — a cell holding a name over its fund house, a bar row
 * holding a label beside its value.
 *
 * `textContent` runs them together, because JSX leaves no whitespace between
 * sibling elements: the exposure row came out as `Financials28.4%`, which reads
 * as one number nobody wrote. Separated, so a label stays a label.
 */
const parts = (node, max = LIMITS.cell) => {
  const kids = Array.from(node?.children ?? []).filter((c) => c.textContent.trim());
  if (kids.length < 2) return clean(node, max);
  return cut(kids.map((c) => clean(c, max)).filter(Boolean).join(' · '), max);
};

const pick = (root, selector) => Array.from(root.querySelectorAll(selector));

/**
 * The value of a row's `data-agent-ref`: `<kind>:<id>`, where kind is one of
 * client, fund, model, stock, goal or manager and id is the route param that
 * row opens (a client's user id, a scheme's symbol, a goal's id).
 *
 * Undefined when the row has no id, so React leaves the attribute off rather
 * than writing `fund:undefined` — a ref the agent would open as if it were real.
 */
export const agentRef = (kind, id) =>
  id == null || String(id).trim() === '' ? undefined : `${kind}:${id}`;

/**
 * A ref longer than this is left out rather than cut. A cut name reads as
 * almost right; a cut id opens the wrong record, or none.
 */
const REF_MAX = 120;

/**
 * A table as a header and its visible rows.
 *
 * Virtualised tables hold spacer rows to keep the scrollbar honest; they carry
 * no cells and are dropped rather than sent as blanks.
 *
 * `refs` exists because rows show names and routes take ids. With only names
 * in front of it, the agent asked to open the client with the largest book
 * guessed an id and opened /clients/1. Where a row declares what it opens,
 * `refs[i]` is that row's `<kind>:<id>`, and null where it declares nothing so
 * the two lists stay aligned. A table where no row declares anything sends no
 * `refs` at all.
 */
function readTable(table) {
  const columns = pick(table, 'thead th').slice(0, LIMITS.columns).map((th) => clean(th, 40));
  const shown = pick(table, 'tbody tr')
    .filter((tr) => tr.cells?.length)
    .slice(0, LIMITS.rows);
  const bodyRows = shown.map((tr) => Array.from(tr.cells).slice(0, LIMITS.columns).map((td) => parts(td)));
  if (!bodyRows.length) return null;

  const refs = shown.map((tr) => {
    const ref = tr.getAttribute('data-agent-ref')?.trim();
    return ref && ref.length <= REF_MAX ? ref : null;
  });

  const total = pick(table, 'tbody tr').filter((tr) => tr.cells?.length).length;
  return {
    columns: columns.filter(Boolean).length ? columns : undefined,
    rows: bodyRows,
    ...(refs.some(Boolean) ? { refs } : null),
    ...(total > bodyRows.length ? { rowsShown: `${bodyRows.length} of ${total} on screen` } : null),
  };
}

/** One card: its title, the table inside it, and the labelled figures beside it. */
function readCard(card) {
  const title = clean(card.querySelector('.card-h b'), 80);
  const table = card.querySelector('table');
  const section = {};
  if (title) section.title = title;

  if (table) {
    const read = readTable(table);
    if (read) Object.assign(section, read);
  } else {
    /* Cards without a table still carry the reading: bar rows, verdict lines,
       the working / not working lists. Their own text, trimmed. */
    const points = pick(card, '.fbar-row, .ww-col li, .attr-row, .sig-row')
      .slice(0, LIMITS.points)
      .map((el) => parts(el, LIMITS.cell))
      .filter(Boolean);
    if (points.length) section.points = points;
    else {
      const body = clean(card.querySelector('.pad'), LIMITS.text);
      if (body) section.text = body;
    }
  }

  return Object.keys(section).length > (section.title ? 1 : 0) ? section : null;
}

/**
 * The page, as a structure the agent can read.
 *
 * `root` is the page region only — never the shell, and never the assistant's
 * own panel: quoting the conversation back into the conversation is how a
 * thread starts answering itself.
 */
export function readScreen(root = document.querySelector('.wrap')) {
  if (!root) return null;

  const digest = {};

  const title = clean(root.querySelector('.page-head h1'), 120);
  const sub = clean(root.querySelector('.page-head .sub'), LIMITS.text);
  if (title) digest.title = title;
  if (sub) digest.subtitle = sub;

  const tab = clean(root.querySelector('.tabs button.on'), 40);
  if (tab) digest.tab = tab;

  /* The headline figures — KPI tiles and the deep-dive's stat strip. Label and
     value stay paired: a column of numbers with the labels somewhere else is
     how a model attributes the expense ratio to the alpha rank. */
  const stats = [
    ...pick(root, '.kpi').map((el) => ({
      label: clean(el.querySelector('h5'), 40),
      value: clean(el.querySelector('.v'), 40),
    })),
    ...pick(root, '.stat-strip > div').map((el) => ({
      label: clean(el.querySelector('h6'), 40),
      value: clean(el.querySelector('b'), 40),
    })),
  ].filter((s) => s.label && s.value).slice(0, LIMITS.stats);
  if (stats.length) digest.stats = stats;

  /* Notes and empty states say what the screen could not show, which is often
     the whole answer to "why is this blank?". */
  const notes = pick(root, '.note, .empty')
    .slice(0, 4)
    .map((el) => clean(el, LIMITS.text))
    .filter(Boolean);
  if (notes.length) digest.notes = notes;

  const sections = pick(root, '.card')
    /* A KPI tile is a card too, and it is already in `stats`. */
    .filter((el) => !el.classList.contains('kpi'))
    .map(readCard)
    .filter(Boolean)
    .slice(0, LIMITS.sections);
  if (sections.length) digest.sections = sections;

  /* A last guard rather than a plan: the caps above should keep this well
     under, and a screen that still runs long loses its tail rather than
     arriving as one oversized value the backend would drop whole. */
  while (JSON.stringify(digest).length > LIMITS.total && digest.sections?.length) {
    digest.sections.pop();
    digest.truncated = true;
  }

  return Object.keys(digest).length ? digest : null;
}
